import { formatDateForDatabase } from "./dateUtils";

export type DashboardPeriod = 'today' | 'week' | 'month' | 'year';

export interface DateRange {
  startDate: string;
  endDate: string;
}

export interface DashboardDateRange extends DateRange {
  effective: DateRange;
  comparison: DateRange;
}

export type DashboardGranularity = 'day' | 'week' | 'month';

const startOfMondayWeek = (date: Date) => {
  const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = result.getDay();
  result.setDate(result.getDate() - (day === 0 ? 6 : day - 1));
  return result;
};

const addDays = (date: Date, days: number) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

const atBusinessNoon = (date: Date, timeZone: string) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(date);
  const value = (type: string) => Number(parts.find((part) => part.type === type)?.value);
  return new Date(value('year'), value('month') - 1, value('day'), 12);
};

const daysInMonth = (year: number, month: number) => new Date(year, month + 1, 0, 12).getDate();

const previousCalendarRange = (period: DashboardPeriod, start: Date, effectiveEnd: Date, isComplete: boolean): DateRange => {
  let previousStart: Date;
  if (period === 'year') previousStart = new Date(start.getFullYear() - 1, 0, 1, 12);
  else if (period === 'month') previousStart = new Date(start.getFullYear(), start.getMonth() - 1, 1, 12);
  else if (period === 'week') previousStart = addDays(start, -7);
  else previousStart = addDays(start, -1);

  const currentOrdinal = Math.round((Date.UTC(effectiveEnd.getFullYear(), effectiveEnd.getMonth(), effectiveEnd.getDate()) - Date.UTC(start.getFullYear(), start.getMonth(), start.getDate())) / 86_400_000);
  const calendarEnd = period === 'year'
    ? new Date(previousStart.getFullYear(), 11, 31, 12)
    : period === 'month'
      ? new Date(previousStart.getFullYear(), previousStart.getMonth() + 1, 0, 12)
      : period === 'week'
        ? addDays(previousStart, 6)
        : previousStart;
  let equivalentEnd: Date;
  if (period === 'month' || (isComplete && period === 'year')) {
    equivalentEnd = calendarEnd;
  } else if (period === 'year') {
    const day = Math.min(effectiveEnd.getDate(), daysInMonth(previousStart.getFullYear(), effectiveEnd.getMonth()));
    equivalentEnd = new Date(previousStart.getFullYear(), effectiveEnd.getMonth(), day, 12);
  } else {
    equivalentEnd = addDays(previousStart, currentOrdinal);
  }
  const previousEnd = new Date(Math.min(equivalentEnd.getTime(), calendarEnd.getTime()));
  return { startDate: formatDateForDatabase(previousStart), endDate: formatDateForDatabase(previousEnd) };
};

export const getDashboardGranularity = (period: DashboardPeriod): DashboardGranularity => {
  if (period === 'year') return 'month';
  if (period === 'month') return 'week';
  return 'day';
};

/**
 * Inclusive business-date range. API consumers must query timestamps using
 * `>= startDate 00:00` and `< endDate + 1 day` in the gym's business timezone.
 */
export const calculateDashboardDateRange = (
  period: DashboardPeriod,
  offset = 0,
  anchor = new Date(),
  timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
): DashboardDateRange => {
  const safeOffset = Math.max(0, Math.trunc(offset));
  const businessAnchor = atBusinessNoon(anchor, timeZone);
  let start: Date;
  let end: Date;

  if (period === 'week') {
    start = startOfMondayWeek(businessAnchor);
    start.setDate(start.getDate() - safeOffset * 7);
    end = addDays(start, 6);
  } else if (period === 'year') {
    const year = businessAnchor.getFullYear() - safeOffset;
    start = new Date(year, 0, 1);
    end = new Date(year, 11, 31);
  } else if (period === 'today') {
    start = addDays(new Date(businessAnchor.getFullYear(), businessAnchor.getMonth(), businessAnchor.getDate(), 12), -safeOffset);
    end = new Date(start);
  } else {
    start = new Date(businessAnchor.getFullYear(), businessAnchor.getMonth() - safeOffset, 1, 12);
    end = new Date(businessAnchor.getFullYear(), businessAnchor.getMonth() - safeOffset + 1, 0, 12);
  }

  const effectiveEnd = safeOffset === 0 && businessAnchor < end ? businessAnchor : end;
  const effective = { startDate: formatDateForDatabase(start), endDate: formatDateForDatabase(effectiveEnd) };

  return {
    startDate: formatDateForDatabase(start),
    endDate: formatDateForDatabase(end),
    effective,
    comparison: previousCalendarRange(period, start, effectiveEnd, effectiveEnd.getTime() === end.getTime()),
  };
};

export const formatDashboardRangeLabel = (
  range: DateRange,
  period: DashboardPeriod,
  locale: string,
) => {
  const language = locale.startsWith('en') ? 'en-GB' : 'fr-FR';
  const start = new Date(`${range.startDate}T12:00:00`);
  const end = new Date(`${range.endDate}T12:00:00`);
  if (period === 'year') return new Intl.DateTimeFormat(language, { year: 'numeric' }).format(start);
  if (period === 'month') return new Intl.DateTimeFormat(language, { month: 'long', year: 'numeric' }).format(start);
  if (period === 'today') return new Intl.DateTimeFormat(language, { day: 'numeric', month: 'long', year: 'numeric' }).format(start);
  const formatter = new Intl.DateTimeFormat(language, { day: 'numeric', month: 'short' });
  return `${formatter.format(start)} – ${formatter.format(end)} ${end.getFullYear()}`;
};

/**
 * Calculate date range based on dashboard filter period
 * @param period - The filter period (today, week, month, year)
 * @returns Object with startDate and endDate in YYYY-MM-DD format
 */
export const calculateDateRange = (period: DashboardPeriod): DateRange => {
  const { startDate, endDate } = calculateDashboardDateRange(period);
  return { startDate, endDate };
};
